import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Contract } from 'src/types/contract.type';
import type { ContractMilestone } from 'src/types/contractMilestone.type';

import MilestoneTable from './MilestoneTable';

function num(n?: number | null) {
    return n == null ? '—' : n.toLocaleString();
}

export default function ContractList({
    title,
    contracts,
    expandedId,
    onToggleExpand,
    onNewContract,
    canEdit,
    milestonesByContractId,
    onCreateMilestone,
    onUpdateMilestone,
    onDeleteMilestone,
}: {
    title: string;
    contracts: Contract[];
    expandedId?: string | null;
    onToggleExpand?: (contractId: string) => void;
    onNewContract: () => void;
    canEdit: boolean;
    // 인라인 확장 영역에서 사용할 milestone data/handlers (v2에서는 선택 사항)
    milestonesByContractId?: Map<string, ContractMilestone[]>;
    onCreateMilestone?: (contractId: string, draft: any) => void;
    onUpdateMilestone?: (contractId: string, milestoneId: string, patch: any) => void;
    onDeleteMilestone?: (contractId: string, milestoneId: string) => void;
}) {
    const nav = useNavigate();

    const rows = useMemo(() => {
        // seq가 별도로 없다면 리스트 순번으로 표시
        return contracts.slice();
    }, [contracts]);

    return (
        <div className="rounded-2xl border bg-white overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="text-sm font-semibold">{title}</div>

                <button
                    className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                    onClick={onNewContract}
                    disabled={!canEdit}
                    title={!canEdit ? '권한이 없습니다' : undefined}
                >
                    + New Contract
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[1200px] //font-mono text-sm">
                    <thead>
                        <tr className="text-left text-gray-500">
                            <th className="px-3 py-3 //w-16">Seq</th>
                            <th className="px-3 py-3 //w-[260px]">계약명</th>
                            <th className="px-3 py-3 //w-[220px]">계약업체</th>
                            <th className="px-3 py-3 //w-20">환종</th>
                            <th className="px-3 py-3 text-right //w-40">계약금액</th>
                            <th className="px-3 py-3 text-right //w-28">환율</th>
                            <th className="px-3 py-3 text-right //w-40">원화환산</th>
                            <th className="px-3 py-3 text-right //w-44">집행(Plan/Actual)</th>
                            <th className="px-3 py-3 text-right //w-44">Progress(Plan/Actual)</th>
                            <th className="px-3 py-3 text-right //w-44">Progress(Plan/Actual)</th>
                            <th className="px-3 py-3 text-right">상세</th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((c, idx) => {
                            const isOpen = expandedId === c.id;
                            const companyName =
                                (c as any).projectCompany?.company?.name ?? (c as any).projectCompany?.name ?? '—';

                            return (
                                <React.Fragment key={c.id}>
                                    <tr
                                        // className="border-t hover:bg-gray-50 cursor-pointer"
                                        className={[
                                            'border-t cursor-pointer //bg-sky-100/40 //hover:bg-sky-200/40',
                                            // isOpen ? 'bg-yacht-50 ring-2 ring-yacht-400 ring-inset' : idx % 2 === 0 ? 'bg-white' : 'bg-yacht-50/50',
                                            // isOpen ? 'bg-yacht-50 outline outline-2 outline-yacht-200 outline-offset-[-2px] hover:bg-yacht-100' : idx % 2 === 0 ? 'bg-white' : 'bg-yacht-50/50',
                                            // isOpen ? 'border-l-4 border-sky-700 bg-sky-50/40' : 'hover:bg-gray-50',
                                            'hover:bg-blue-50/60',
                                        ].join(' ')}
                                        onClick={() => onToggleExpand?.(c.id)}
                                    >
                                        <td className="px-3 py-3">{idx + 1}</td>
                                        <td className="px-3 py-3">
                                            <div className="font-medium">{(c as any).contractName ?? '—'}</div>
                                            {/* <div className="text-xs text-gray-500">
                                                {isOpen ? '▼ 펼쳐짐' : '▶ 펼치기'}
                                            </div> */}
                                        </td>
                                        <td className="px-3 py-3">{companyName}</td>
                                        <td className="px-3 py-3">{((c as any).currency ?? '—') as any}</td>
                                        <td className="px-3 py-3 text-right">
                                            {num(Number((c as any).contractAmount ?? 0))}
                                        </td>
                                        <td className="px-3 py-3 text-right">—</td>
                                        <td className="px-3 py-3 text-right">—</td>
                                        <td className="px-3 py-3 text-right text-gray-500">— / —</td>
                                        <td className="px-3 py-3 text-right text-gray-500">— / —</td>
                                        <td className="px-3 py-3 text-right text-gray-500">— / —</td>
                                        <td className="px-3 py-3 text-right">
                                            <button
                                                type="button"
                                                className="text-xs px-2.5 py-1.5 rounded-lg border bg-white hover:bg-gray-50"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // ✅ expand 토글 방지
                                                    nav(`${c.id}`); // ✅ ContractDetailPage로 이동
                                                }}
                                            >
                                                상세보기
                                            </button>
                                        </td>
                                    </tr>

                                    {isOpen && !!milestonesByContractId && (
                                        <tr
                                            className={
                                                isOpen ? 'border-l-4 border-sky-700 bg-sky-50/40' : 'hover:bg-gray-50'
                                            }
                                        >
                                            <td colSpan={11} className="px-3 py-3 text-xs">
                                                <MilestoneTable
                                                    contract={c}
                                                    items={milestonesByContractId.get(c.id) ?? []}
                                                    onCreate={(draft) => onCreateMilestone?.(c.id, draft)}
                                                    onUpdate={(id, patch) => onUpdateMilestone?.(c.id, id, patch)}
                                                    onDelete={(id) => onDeleteMilestone?.(c.id, id)}
                                                    canEdit={canEdit}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={11} className="px-3 py-10 text-center text-gray-500">
                                    계약이 없습니다.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
